import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { FormattedMessage, useIntl } from "react-intl";
import { useAuthStore } from "@/lib/stores/authStore";
import { showToast } from "@/lib/utils/toast";
import { ExerciseLayout } from "@/components/ui/exercise-layout";
import { useEffect, useState } from "react";
import type { Gender } from "@/lib/types/auth";
import { useNotifications } from "@/hooks/useNotifications";
import { FiEdit2 } from "react-icons/fi";
import { IoClose } from "react-icons/io5";
import ConfirmationModal from "@/components/ui/confirmation-modal";
import { isApiError } from "@/lib/api/client";
// Study temporarily disabled
// import { StudyProfileSection } from "@/components/study/StudyProfileSection";
import { ProfileHeader } from "@/components/ProfileHeader";
import { ProfileCard } from "@/components/ProfileCard";
import { ProfileInfoRow } from "@/components/ProfileInfoRow";

export const Route = createFileRoute("/(content)/profile")({
  component: ProfileComponent,
  head: () => ({
    meta: [
      {
        title: "Profile - HAMD",
      },
    ],
  }),
  beforeLoad: () => ({
    title: "profile.title",
    navbar: {
      showTitle: true,
    },
  }),
});

function ProfileComponent() {
  const navigate = useNavigate();
  const intl = useIntl();
  const { user, logout, updateProfile, deleteAccount, isLoading } = useAuthStore();
  const { registerToken } = useNotifications();
  const [nickname, setNickname] = useState<string>("");
  const [selectedGender, setSelectedGender] = useState<
    Gender | null | undefined
  >(undefined);
  const [isEditMode, setIsEditMode] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [nicknameError, setNicknameError] = useState<string | null>(null);
  const [notifPermission, setNotifPermission] = useState<NotificationPermission | 'unsupported'>(
    () => ('Notification' in window ? Notification.permission : 'unsupported')
  );
  const [isEnablingNotif, setIsEnablingNotif] = useState(false);

  useEffect(() => {
    setNickname(user?.nickname ?? "");
    setSelectedGender(user?.gender ?? undefined);
    setHasChanges(false);
    setIsEditMode(false);
    setNicknameError(null);
  }, [user?.nickname, user?.gender]);

  const handleEnableNotifications = async () => {
    if (notifPermission === 'denied') {
      showToast.info(intl.formatMessage({ id: 'profile.notifications.denied' }));
      return;
    }
    setIsEnablingNotif(true);
    const result = await registerToken();
    setIsEnablingNotif(false);
    if (result === 'granted') {
      setNotifPermission('granted');
      showToast.success(intl.formatMessage({ id: 'toast.profile.notificationsEnabled' }));
    } else if (result === 'denied') {
      setNotifPermission('denied');
      showToast.info(intl.formatMessage({ id: 'profile.notifications.denied' }));
    } else {
      showToast.error(intl.formatMessage({ id: 'toast.profile.notificationsError' }));
    }
  };

  const handleLogout = async () => {
    await logout();
    showToast.success(intl.formatMessage({ id: "toast.auth.logoutSuccess" }));
    navigate({ to: "/auth/login" });
  };

  const handleNicknameChange = (value: string) => {
    setNickname(value);
    setHasChanges(value !== user?.nickname || selectedGender !== user?.gender);
    setNicknameError(null); // Clear error when user types
  };

  const handleGenderChange = (gender: Gender | null) => {
    setSelectedGender(gender);
    setHasChanges(gender !== user?.gender || nickname !== user?.nickname);
  };

  const handleCancelEdit = () => {
    setNickname(user?.nickname ?? "");
    setSelectedGender(user?.gender ?? undefined);
    setIsEditMode(false);
    setHasChanges(false);
    setNicknameError(null);
  };

  const getGenderDisplayText = (gender: Gender | null | undefined) => {
    if (gender === "male") {
      return intl.formatMessage({ id: "register.gender.male" });
    }
    if (gender === "female") {
      return intl.formatMessage({ id: "register.gender.female" });
    }
    return intl.formatMessage({ id: "profile.gender.preferNotToSay" });
  };

  const handleSaveProfile = async () => {
    setNicknameError(null);
    
    if (!nickname.trim()) {
      setNicknameError(
        intl.formatMessage({ id: "validation.nickname.required" })
      );
      return;
    }
    
    if (nickname.trim().length < 3) {
      setNicknameError(
        intl.formatMessage({ id: "validation.nickname.minLength" })
      );
      return;
    }
    
    if (nickname.trim().length > 20) {
      setNicknameError(
        intl.formatMessage({ id: "validation.nickname.maxLength" })
      );
      return;
    }
    
    try {
      const updates: { nickname?: string; gender?: Gender | null } = {};

      if (nickname !== user?.nickname) {
        updates.nickname = nickname.trim();
      }
      if (selectedGender !== user?.gender) {
        updates.gender = selectedGender ?? null;
      }

      await updateProfile(updates);
      showToast.success(
        intl.formatMessage({ id: "toast.profile.updateSuccess" }),
      );
      setHasChanges(false);
      setIsEditMode(false);
    } catch (error) {
      // Show specific error message from backend if available
      const errorMessage = isApiError(error) 
        ? error.message 
        : intl.formatMessage({ id: "toast.profile.updateError" });
      
      // Show nickname-related errors under the input field
      if (isApiError(error) && (error.status === 409 || error.message.toLowerCase().includes('nickname'))) {
        setNicknameError(errorMessage);
      } else {
        showToast.error(errorMessage);
      }
    }
  };

  const handleDeleteAccount = async () => {
    try {
      await deleteAccount();
      showToast.success(
        intl.formatMessage({ id: "toast.profile.deleteSuccess" }),
      );
      navigate({ to: "/auth/login" });
    } catch (error) {
      // Show specific error message from backend if available
      const errorMessage = isApiError(error) 
        ? error.message 
        : intl.formatMessage({ id: "toast.profile.deleteError" });
      showToast.error(errorMessage);
    }
  };

  if (!user) {
    return (
      <div className="px-6 py-8 space-y-6">
        <ProfileHeader
          subtitle={<FormattedMessage id="profile.notLoggedIn" />}
        />

        <ProfileCard className="p-4 space-y-3">
          <Button className="w-full" onClick={() => navigate({ to: "/auth/login" })}>
            <FormattedMessage id="profile.loginToView" />
          </Button>
          {notifPermission !== 'granted' && notifPermission !== 'unsupported' && (
            <Button
              onClick={handleEnableNotifications}
              className="w-full"
              variant="secondary"
              disabled={isEnablingNotif}
            >
              <FormattedMessage id="profile.enableNotifications" />
            </Button>
          )}
        </ProfileCard>

        {/* <ProfileCard className="p-4">
          <StudyProfileSection />
        </ProfileCard> */}
      </div>
    );
  }

  return (
    <ExerciseLayout
      contentClassName="space-y-5 py-6"
      actions={
        <div className="space-y-3">
          {notifPermission !== 'granted' && notifPermission !== 'unsupported' && (
            <Button
              onClick={handleEnableNotifications}
              className="w-full"
              variant="secondary"
              disabled={isEnablingNotif}
            >
              <FormattedMessage id="profile.enableNotifications" />
            </Button>
          )}
          <Button
            onClick={handleLogout}
            className="w-full"
            disabled={isLoading}
          >
            <FormattedMessage id="profile.logout" />
          </Button>
          <Button
            onClick={() => setShowDeleteConfirmation(true)}
            className="w-full"
            variant="secondary"
            disabled={isLoading}
          >
            <FormattedMessage id="profile.deleteAccount" />
          </Button>
        </div>
      }
    >
      <ProfileHeader
        name={user.nickname}
        subtitle={<FormattedMessage id="profile.description" />}
      />

      <ProfileCard>
        <ProfileInfoRow
          label={<FormattedMessage id="profile.nickname" />}
          action={
            !isEditMode ? (
              <button
                onClick={() => setIsEditMode(true)}
                disabled={isLoading}
                className="p-1.5 rounded-lg hover:bg-secondary transition-colors"
                aria-label="Edit Profile"
              >
                <FiEdit2 size={16} className="text-primary" />
              </button>
            ) : (
              <div className="flex items-center gap-2">
                {hasChanges && (
                  <Button onClick={handleSaveProfile} disabled={isLoading} size="sm" className="text-micro h-8 px-3">
                    <FormattedMessage id="profile.saveChanges" />
                  </Button>
                )}
                <button onClick={handleCancelEdit} disabled={isLoading} className="p-1.5 rounded-lg hover:bg-secondary transition-colors" aria-label="Cancel">
                  <IoClose size={18} className="text-dark-gray" />
                </button>
              </div>
            )
          }
        >
          {isEditMode ? (
            <div>
              <input
                type="text"
                value={nickname}
                onChange={(e) => handleNicknameChange(e.target.value)}
                placeholder={intl.formatMessage({ id: "register.nickname.placeholder" })}
                className={`w-full h-11 rounded-xl px-3 text-small bg-white text-primary border-2 focus:outline-none ${
                  nicknameError ? "border-red-400" : "border-primary"
                }`}
                disabled={isLoading}
              />
              {nicknameError && <p className="text-xs text-red-400 mt-1">{nicknameError}</p>}
            </div>
          ) : (
            <p className="text-small text-primary">{user.nickname}</p>
          )}
        </ProfileInfoRow>

        <ProfileInfoRow divider label={<FormattedMessage id="profile.gender" />}>
          {isEditMode ? (
            <div className="space-y-2 pt-1">
              <div className="grid grid-cols-2 gap-2">
                {(["male", "female"] as Gender[]).map((gender) => (
                  <button
                    key={gender}
                    type="button"
                    onClick={() => handleGenderChange(gender)}
                    className={`h-11 rounded-xl text-small transition-all duration-200 ${
                      selectedGender === gender ? "bg-primary text-white" : "bg-secondary text-dark-gray"
                    }`}
                    disabled={isLoading}
                  >
                    {gender === "male" && <FormattedMessage id="register.gender.male" />}
                    {gender === "female" && <FormattedMessage id="register.gender.female" />}
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={() => handleGenderChange(null)}
                className={`w-full h-11 rounded-xl text-small transition-all duration-200 ${
                  selectedGender === null ? "bg-primary text-white" : "bg-secondary text-dark-gray"
                }`}
                disabled={isLoading}
              >
                <FormattedMessage id="profile.gender.preferNotToSay" />
              </button>
            </div>
          ) : (
            <p className="text-small text-primary">{getGenderDisplayText(user.gender)}</p>
          )}
        </ProfileInfoRow>

        <ProfileInfoRow
          divider
          label={<FormattedMessage id="profile.audience" />}
          value={
            user.audience
              ? intl.formatMessage({ id: `register.audience.${user.audience}` })
              : ""
          }
        />
      </ProfileCard>

      {/* <ProfileCard className="p-4">
        <StudyProfileSection />
      </ProfileCard> */}

      <ConfirmationModal
        isOpen={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        onConfirm={handleDeleteAccount}
        titleKey="profile.deleteAccount.confirmTitle"
        messageKey="profile.deleteAccount.confirmMessage"
        confirmButtonTextKey="profile.deleteAccount.confirm"
        cancelButtonTextKey="common.cancel"
      />
    </ExerciseLayout>
  );
}
